import React, { useState, forwardRef } from "react";
import cn from "classnames";
import { InputAdornment, TextField } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import styles from "./Input.module.scss";
import Icon from "../Icon/Icon";
import { InputProps } from "./types";

const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    required,
    icon,
    className,
    defaultValue,
    borderColor,
    indicator,
    type = "text",
    showEyeIcon = false,
    max,
    min,
    IconBg,
    inputProps,
    ...restProps
  } = props;

  const hasIcon = Boolean(icon);

  const [viewer, setViewer] = useState<boolean>(!showEyeIcon);
  const handleVisibility = () => {
    setViewer(!viewer);
  };

  const showPicker = (e: any) => {
    if (type === "date" || type === "time") e?.target?.showPicker();
  };

  return (
    <TextField
      {...restProps}
      onClick={showPicker}
      type={showEyeIcon ? (viewer ? "text" : "password") : type}
      defaultValue={defaultValue}
      sx={{
        ".MuiOutlinedInput-notchedOutline": {
          borderStyle: "none",
        },
        // Charcoal field from the app tokens (Styles/tokens.scss); light theme swaps the tokens.
        backgroundColor: "var(--dt-control)",
        border: `1px solid ${borderColor ?? "var(--dt-control-border)"}`,
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        "&:hover": { borderColor: borderColor ?? "var(--dt-muted)" },
        "&:focus-within": {
          borderColor: "var(--dt-muted)",
          boxShadow: "0 0 0 3px color-mix(in srgb, var(--dt-muted) 22%, transparent)",
        },
        "&:has(.Mui-error)": { borderColor: "var(--dt-danger)" },
        "&:has(.Mui-disabled)": {
          backgroundColor: "var(--dt-zebra)",
          borderColor: "var(--dt-border)",
        },
      }}
      classes={{
        root: cn(styles.input, hasIcon && styles.with__icon, className),
      }}
      required={required}
      inputRef={ref}
      InputProps={{
        inputProps: { ...inputProps, maxLength: max },
        disableUnderline: true,
        endAdornment:
          (showEyeIcon && (
            <InputAdornment position="end">
              <IconButton onClick={handleVisibility}>
                {viewer ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          )) ||
          (indicator && (
            <InputAdornment className={styles.input__indicator} position="end">
              {indicator}
            </InputAdornment>
          )) ||
          null,
        startAdornment: icon && (
          <InputAdornment
            sx={{
              height: "auto",
              maxHeight: "none",
              margin: 0,
              padding: "0 4px 0 12px",
              // IconBg is kept for callers; the prefix now sits in the field like "$".
              backgroundColor: IconBg && IconBg !== "#e9f0fd" ? IconBg : "transparent",
              color: "var(--dt-muted)",
              fontSize: "13px",
            }}
            position="start"
          >
            {typeof icon === "string" ? (
              icon
            ) : (
              <Icon component={icon} color="transparent" />
            )}
          </InputAdornment>
        ),
      }}
    />
  );
});

Input.displayName = "Input";

export default Input;
